import React, { useMemo, useState, useCallback } from 'react';
import { Button } from 'reactstrap';
import ReactTable from 'react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDownload,
    faFile,
    faFilePdf,
    faFileImage,
    faFileWord,
    faFileExcel,
    faFilePowerpoint,
    faFileArchive,
    faFileAlt
} from '@fortawesome/free-solid-svg-icons';

const normalizeValue = (value) => String(value || '').trim().toLowerCase();
const normalizeWhitespace = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const getHouseholdKeyFromPath = (path) => {
    const parts = String(path || '')
        .split('/')
        .map(part => normalizeValue(part))
        .filter(Boolean);
    const householdsIdx = parts.indexOf('households');
    return householdsIdx >= 0 ? parts[householdsIdx + 1] || '' : '';
};

const getFolderDisplay = (entry) => {
    const text = normalizeWhitespace(entry?.text);
    const cleaned = text.replace(/\/+$/, '');
    return cleaned || '/';
};

const toGridFolderDisplay = (folder = '') => (folder === '/' ? '' : folder);

const getParentFolderFromFilePath = (path = '') => {
    const rawParts = String(path || '').split('/').filter(Boolean);
    const householdsIdx = rawParts.findIndex(part => normalizeValue(part) === 'households');
    if (householdsIdx < 0) return '/';

    const afterHousehold = rawParts.slice(householdsIdx + 2);
    if (afterHousehold.length <= 1) return '/';

    const folderParts = afterHousehold.slice(0, -1);
    const folder = folderParts.join('/').trim();
    return folder || '/';
};

const isFolderEntry = (entry) => {
    if (Array.isArray(entry?.items)) return true;
    const text = String(entry?.text || '').trim();
    const path = String(entry?.path || '').trim();
    return text.endsWith('/') || path.endsWith('/');
};

const formatTimestamp = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const datePart = date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit'
    });
    const timePart = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    }).toLowerCase();
    return `${datePart} ${timePart}`;
};

const isPdfFileName = (value = '') => String(value || '').trim().toLowerCase().endsWith('.pdf');

const getFileExtension = (name = '', path = '') => {
    const source = String(name || path || '').toLowerCase();
    const dotIndex = source.lastIndexOf('.');
    if (dotIndex === -1) return '';
    return source.slice(dotIndex + 1);
};

const getFileIcon = (name = '', path = '') => {
    const ext = getFileExtension(name, path);
    if (ext === 'pdf') return faFilePdf;
    if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp', 'tif', 'tiff'].includes(ext)) return faFileImage;
    if (['doc', 'docx'].includes(ext)) return faFileWord;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return faFileExcel;
    if (['ppt', 'pptx'].includes(ext)) return faFilePowerpoint;
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return faFileArchive;
    if (['txt', 'rtf', 'md'].includes(ext)) return faFileAlt;
    return faFile;
};

const getFileIconClass = (name = '', path = '') => {
    const ext = getFileExtension(name, path);
    if (ext === 'pdf') return 'contactsDocFileIconPdf';
    if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp', 'tif', 'tiff'].includes(ext)) return 'contactsDocFileIconImage';
    return 'contactsDocFileIconDefault';
};

const filterCaseInsensitive = (filter, row) => {
    const id = filter.pivotId || filter.id;
    const content = row[id];
    if (typeof content !== 'undefined' && content !== null) {
        return normalizeValue(content).includes(normalizeValue(filter.value));
    }
    return true;
};

const ContactsTableView = ({
    folderEntries = [],
    sourceAccounts = [],
    onDocumentAction,
    isDocumentPending
}) => {
    const [pageSize, setPageSizeState] = useState(
        localStorage.getItem('contactsDocumentsGridPageSize') != null
            ? parseInt(localStorage.getItem('contactsDocumentsGridPageSize'))
            : 20
    );

    const householdDisplayMap = useMemo(() => {
        const map = new Map();
        (sourceAccounts || []).forEach((account) => {
            const label = normalizeWhitespace(account);
            const key = normalizeValue(label);
            if (key && !map.has(key)) map.set(key, label);
        });
        return map;
    }, [sourceAccounts]);

    const rows = useMemo(() => {
        const output = [];
        (folderEntries || []).forEach((entry) => {
            const householdKey = getHouseholdKeyFromPath(entry?.path);
            if (!householdKey) return;
            const household = householdDisplayMap.get(householdKey) || householdKey;

            if (isFolderEntry(entry)) {
                const folder = getFolderDisplay(entry);
                const items = Array.isArray(entry?.items) ? entry.items : [];

                items.forEach((item) => {
                    output.push({
                        household,
                        folder: toGridFolderDisplay(folder),
                        file: item?.text || '',
                        fileName: item?.text || '',
                        filePath: item?.path || '',
                        timestamp: formatTimestamp(item?.uploadDate),
                        uploadedBy: item?.uploadedBy || '',
                        description: item?.description || '',
                        downloadPath: item?.path || ''
                    });
                });
                return;
            }

            output.push({
                household,
                folder: toGridFolderDisplay(getParentFolderFromFilePath(entry?.path)),
                file: entry?.text || '',
                fileName: entry?.text || '',
                filePath: entry?.path || '',
                timestamp: formatTimestamp(entry?.uploadDate),
                uploadedBy: entry?.uploadedBy || '',
                description: entry?.description || '',
                downloadPath: entry?.path || ''
            });
        });
        return output;
    }, [folderEntries, householdDisplayMap]);

    const setPageSize = useCallback((nextPageSize) => {
        const parsed = parseInt(nextPageSize);
        const safePageSize = Number.isNaN(parsed) ? 20 : parsed;
        setPageSizeState(safePageSize);
        localStorage.setItem('contactsDocumentsGridPageSize', safePageSize);
    }, []);

    const columns = useMemo(() => ([
        {
            Header: '',
            id: 'download',
            accessor: 'downloadPath',
            filterable: false,
            sortable: false,
            width: 58,
            headerStyle: { textAlign: 'center' },
            style: { textAlign: 'center' },
            Cell: ({ original }) => (
                <Button
                    size="sm"
                    color="primary"
                    // outline
                    disabled={!original.downloadPath || (typeof isDocumentPending === 'function' && isDocumentPending(original.fileName, original.downloadPath))}
                    title="Download"
                    onClick={() => {
                        if (typeof onDocumentAction === 'function') {
                            onDocumentAction({
                                name: original.fileName,
                                path: original.downloadPath,
                                source: 'download'
                            });
                        }
                    }}
                >
                    <FontAwesomeIcon icon={faDownload} />
                </Button>
            )
        },
        {
            Header: 'File',
            accessor: 'file',
            filterable: true,
            headerStyle: { textAlign: 'left' },
            style: { textAlign: 'left' },
            minWidth: 320,
            Cell: ({ original, value }) => {
                const pending = typeof isDocumentPending === 'function'
                    ? isDocumentPending(original.fileName, original.filePath)
                    : false;

                const fileNode = !isPdfFileName(original.fileName) ? (
                    <span>{value}</span>
                ) : (
                    <Button
                        color="link"
                        className="p-0"
                        disabled={!original.filePath || pending}
                        title={value}
                        onClick={() => {
                            if (typeof onDocumentAction === 'function') {
                                onDocumentAction({
                                    name: original.fileName,
                                    path: original.filePath,
                                    source: 'file'
                                });
                            }
                        }}
                    >
                        {value}
                    </Button>
                );

                return (
                    <span className="d-inline-flex align-items-center">
                        <FontAwesomeIcon
                            icon={getFileIcon(original.fileName, original.filePath)}
                            className={`contactsDocFileIcon ${getFileIconClass(original.fileName, original.filePath)} mr-2`}
                        />
                        {fileNode}
                    </span>
                );
            }
        },
        {
            Header: 'Household',
            accessor: 'household',
            filterable: true,
            headerStyle: { textAlign: 'left' },
            style: { textAlign: 'left' },
            minWidth: 220
        },
        {
            Header: 'Folder',
            accessor: 'folder',
            filterable: true,
            headerStyle: { textAlign: 'left' },
            style: { textAlign: 'left' },
            minWidth: 160
        },
        {
            Header: 'Description',
            accessor: 'description',
            filterable: true,
            headerStyle: { textAlign: 'left' },
            style: { textAlign: 'left' },
            minWidth: 180
        },
        {
            Header: 'Uploaded By',
            accessor: 'uploadedBy',
            filterable: true,
            headerStyle: { textAlign: 'left' },
            style: { textAlign: 'left' },
            minWidth: 220
        },
        {
            Header: 'Timestamp',
            accessor: 'timestamp',
            filterable: true,
            headerStyle: { textAlign: 'left' },
            style: { textAlign: 'left' },
            width: 160
        }
    ]), [onDocumentAction, isDocumentPending]);

    return (
        <ReactTable
            data={rows}
            columns={columns}
            defaultFilterMethod={filterCaseInsensitive}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            pageSizeOptions={[10, 20, 50, 100, 250]}
            showPageSizeOptions={true}
            minRows={0}
            noDataText="No documents available"
        />
    );
};

export default ContactsTableView;
